import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { getRouterSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, filter, from, map, of, switchMap } from 'rxjs';
import { ApiTypes, DealerFeature, ImprintPageFeature, TrackingFeature } from '../../..';
import { VehicleApiService } from '../../api/vehicle-api.service';
import { isHWS, PLATFORM_TOKEN } from '../../providers/platform.token';
import { CookieService } from '../../services/cookie.service';
import { VehicleFavoritesService } from '../../services/vehicle-favorites.service';
import { extractAdIdFromVehicleSlug } from '../../services/vehicle-favorites.utility';
import { vehicleActions } from './vehicle.actions';
import { HistoryEntries, HistoryFilter, QuickfilterEntry, vehicleState } from './vehicle.reducer';

@Injectable()
export class VehicleEffects {
    protected readonly QUICKFILTER_COOKIE_NAME = 'px-quickfilter';
    protected readonly QUICKFILTER_MAX_AGE_DAYS = 30;
    protected readonly QUICKFILTER_MAX_ENTRIES = 2;
    protected readonly ADDITIONAL_FILTERS_OFFSET = 2;
    protected readonly MAX_DISPLAY_LENGTH = 10;
    protected readonly cookieService = inject(CookieService);
    actions$ = inject(Actions);
    vehicleApi = inject(VehicleApiService);
    vehicleFavoritesService = inject(VehicleFavoritesService);
    platformId = inject(PLATFORM_ID);
    store = inject(Store);
    activatedRoute = inject(ActivatedRoute);
    platformToken = inject(PLATFORM_TOKEN);
    isHWS = isHWS();
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    initVehicleMarket = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.init),
            switchMap(() => {
                return [vehicleActions.loadNumberOfVehicles(), vehicleActions.loadVehicleList()];
            }),
        );
    });

    loadNumberOfVehicles = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadNumberOfVehicles),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectFilterSelected),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getNumberOfVehicles({
                        ...filter,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                    }),
                ).pipe(
                    switchMap((response) => {
                        return [vehicleActions.loadNumberOfVehiclesSuccess(response)];
                    }),
                    catchError((error) => of(vehicleActions.loadNumberOfVehiclesFailure({ error }))),
                );
            }),
        );
    });

    loadFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadFilter),
            concatLatestFrom(() => [this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null)]),
            switchMap(([_, dealer]) => {
                const dealerId = dealer ? dealer.id : undefined;

                return from(this.vehicleApi.getFilter(dealerId)).pipe(
                    switchMap((filterInputs) => {
                        return [vehicleActions.loadFilterSuccess({ filterInputs })];
                    }),
                    catchError((error) => of(vehicleActions.loadFilterFailure({ error }))),
                );
            }),
        );
    });

    loadQuickSearch = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadQuickSearch),
            switchMap(() => {
                return [
                    vehicleActions.loadNumberOfVehicles(),
                    vehicleActions.loadHistorys(),
                    vehicleActions.resetFilter(),
                ];
            }),
        );
    });

    loadVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadVehicleList),
            concatLatestFrom(() => [
                this.store.select(getRouterSelectors().selectQueryParam('page')),
                this.store.select(vehicleState.selectFilterSelected),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),

            switchMap(([_, page, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getVehicleList({
                        page: page ? Number(page) : 1,
                        limit: 12,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                        ...filter,
                    }),
                ).pipe(
                    switchMap((list) => {
                        return [vehicleActions.loadVehicleListSuccess({ list })];
                    }),
                    catchError((error) => of(vehicleActions.loadVehicleListFailure({ error }))),
                );
            }),
        );
    });

    loadDetailVehicle$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadVehicleDetail),
            switchMap(({ slug }) => {
                if (!slug) {
                    throw new Error('Slug is not defined');
                }

                return from(this.vehicleApi.getVehicleDetail(slug)).pipe(
                    switchMap((vehicle) => {
                        const baseActions = [
                            vehicleActions.loadVehicleDetailSuccess({ vehicle }),
                            ImprintPageFeature.actions.loadDealerImprintOverlayBySlug({ slug: vehicle.dealer.slug }),
                            vehicleActions.loadSimilarVehicleList(),
                        ];
                        return this.isBrowser
                            ? [
                                  ...baseActions,
                                  vehicleActions.addVehicleView({ slug }),
                                  vehicleActions.loadSocialProof({ slug }),
                              ]
                            : baseActions;
                    }),
                    catchError((error) => of(vehicleActions.loadVehicleDetailFailure({ error }))),
                );
            }),
        );
    });

    addVehicleView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addVehicleView),
            switchMap(({ slug }) => {
                if (!slug) {
                    throw new Error('Slug is not defined');
                }
                return from(this.vehicleApi.addVehicleView(slug)).pipe(
                    switchMap(() => {
                        return [vehicleActions.addVehicleViewSuccess()];
                    }),
                    catchError((error) => of(vehicleActions.addVehicleViewFailure({ error }))),
                );
            }),
        );
    });

    loadSocialProof$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadSocialProof),
            switchMap(({ slug }) => {
                if (!slug) {
                    throw new Error('Slug is not defined');
                }
                return from(this.vehicleApi.getNumberOfVehiclesViews(slug)).pipe(
                    switchMap((viewResult) => {
                        return [vehicleActions.loadSocialProofSuccess(viewResult)];
                    }),
                    catchError((error) => of(vehicleActions.loadSocialProofFailure({ error }))),
                );
            }),
        );
    });

    loadSimilarVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadSimilarVehicleList),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectLoadedDetail),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, vehicle, dealer]) => {
                const price = vehicle.price.buy;
                let year = vehicle.modelyear;
                let { mileage } = vehicle.details;
                if (year < 1900) {
                    year = new Date().getFullYear() - 1;
                }
                if (mileage - 5000 < 0) {
                    mileage = 5000;
                }
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getVehicleList(
                        {
                            page: 1,
                            limit: 7,
                            ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                            model: vehicle.model ? [vehicle.model] : undefined,
                            price: { min: Math.round(price * 0.8), max: Math.round(price * 1.2) },
                            firstRegistrationYear: { min: year - 1, max: year + 1 },
                            mileage: mileage ? { min: mileage - 5000, max: mileage + 5000 } : undefined,
                        },
                        vehicle.id,
                    ),
                ).pipe(
                    switchMap((list) => {
                        return [vehicleActions.loadSimilarVehicleListSuccess({ list })];
                    }),
                    catchError((error) => of(vehicleActions.loadSimilarVehicleListFailure({ error }))),
                );
            }),
        );
    });

    loadFavorites$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadFavorites),
            concatLatestFrom(() => [this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null)]),
            switchMap(([, dealer]) => {
                const scope = this.vehicleFavoritesService.getScope();
                const favoritesList = this.vehicleFavoritesService.getFavorites(scope);
                const adIds = favoritesList.map((favorite) => extractAdIdFromVehicleSlug(favorite.slug));
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                const dealerId = dealer ? [dealer.id, ...subdealerIds].join(',') : undefined;

                return from(this.vehicleApi.getFavorites(adIds ?? [], dealerId)).pipe(
                    switchMap((favorites) => {
                        this.vehicleFavoritesService.pruneStale(scope, favorites.vehicles);
                        return [vehicleActions.loadFavoritesSuccess({ favorites })];
                    }),
                    catchError((error) => of(vehicleActions.loadFavoritesFailure({ error }))),
                );
            }),
        );
    });

    addFilterAndRemoveFilter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addFilterAndRemoveFilter),
            switchMap(({ filterObject, trackingKeys, historyMessage, removeFilter }) => {
                return [
                    vehicleActions.addFilter({ filterObject, trackingKeys, historyMessage }),
                    vehicleActions.removeFilter({
                        removeFilterObject: Object.keys(removeFilter) as Array<keyof ApiTypes.FilterObject>,
                        trackingKeys,
                    }),
                ];
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnReset = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.resetFilter),
            concatLatestFrom(() => [
                this.store
                    .select(vehicleState.selectTrackingKeys)
                    .pipe(map((keys) => Object.keys(keys).filter((key) => keys[key]))),
                this.store
                    .select(vehicleState.selectTrackingKeysPreview)
                    .pipe(map((keys) => Object.keys(keys).filter((key) => keys[key]))),
            ]),
            switchMap(([_, trackingKeys, trackingKeysPreview]) => {
                return [
                    vehicleActions.loadFilterPreviewNumberOfVehicles(),
                    ...[...trackingKeys, ...trackingKeysPreview].map((key) =>
                        TrackingFeature.actions.filterTracking({ filterType: key }),
                    ),
                    vehicleActions.resetTrackingKeys(),
                ];
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnAdd = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addFilter),
            debounceTime(1000),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerTrackingOnAddFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addFilter),
            switchMap(({ trackingKeys }) => {
                return trackingKeys.map((key) => TrackingFeature.actions.filterTracking({ filterType: key }));
            }),
        );
    });

    triggerNumberOfVehiclesOnResetPreviewFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.resetPreviewFilter),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnRemove = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.removeFilter),
            debounceTime(1000),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerTrackingOnRemoveFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.removeFilter),
            switchMap(({ trackingKeys }) => {
                return trackingKeys.map((filterType) => TrackingFeature.actions.filterTracking({ filterType }));
            }),
        );
    });

    loadPreviewNumberOfVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadFilterPreviewNumberOfVehicles),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectFilterSelectedPreview),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getNumberOfVehicles({
                        ...filter,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                    }),
                ).pipe(
                    switchMap((response) => {
                        return [vehicleActions.loadFilterPreviewNumberOfVehiclesSuccess(response)];
                    }),
                    catchError((error) => of(vehicleActions.loadFilterPreviewNumberOfVehiclesFailure({ error }))),
                );
            }),
        );
    });

    openFilter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.openFilter),
            switchMap(() => {
                return [
                    vehicleActions.loadFilterPreviewNumberOfVehicles(),
                    TrackingFeature.actions.overlayTracking({ label: 'filter' }),
                ];
            }),
        );
    });

    loadHistorys = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadHistorys),
            filter(() => isPlatformBrowser(this.platformId)),
            concatLatestFrom(() => [this.store.select(vehicleState.selectFilterHistorys)]),
            switchMap(([_, historys]) => {
                if (!historys) {
                    return [vehicleActions.setHistoryFailure()];
                }
                try {
                    if (historys.length > 0) {
                        return [vehicleActions.setHistorySuccess({ historys })];
                    }

                    const existingCookie = this.cookieService.get(this.QUICKFILTER_COOKIE_NAME);
                    const existingCookieContent: QuickfilterEntry[] = existingCookie ? JSON.parse(existingCookie) : [];

                    if (existingCookieContent.length === 0) {
                        return [vehicleActions.setHistoryFailure()];
                    }

                    return [vehicleActions.setHistorySuccess({ historys: existingCookieContent })];
                } catch {
                    return [vehicleActions.setHistoryFailure()];
                }
            }),
        );
    });

    setHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(vehicleActions.setHistory),
            filter((): boolean => isPlatformBrowser(this.platformId)),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectCurrentHistoryEntries),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([action, currentHistoryEntries, dealer]) => {
                try {
                    return this.handleSetHistory(action.history, currentHistoryEntries, dealer?.slug);
                } catch {
                    return [vehicleActions.setHistoryFailure()];
                }
            }),
        ),
    );

    private handleSetHistory(
        filter: HistoryFilter | null | undefined,
        currentHistoryEntries: HistoryEntries,
        slug?: string,
    ): Array<ReturnType<typeof vehicleActions.setHistorySuccess | typeof vehicleActions.setHistoryFailure>> {
        if (!this.isValidFilter(filter)) {
            return [vehicleActions.setHistoryFailure()];
        }

        const newEntry = this.createQuickfilterEntry(filter, currentHistoryEntries);

        const updatedHistory = this.updateCookieHistory(newEntry, slug);

        return [
            vehicleActions.setHistorySuccess({
                historys: updatedHistory,
            }),
        ];
    }

    private isValidFilter(filter: HistoryFilter | null | undefined): filter is HistoryFilter {
        // oxlint-disable-next-line no-implicit-coercion
        return !!filter && Object.keys(filter).length > 0;
    }

    private createQuickfilterEntry(filter: HistoryFilter, currentHistoryEntries: HistoryEntries): QuickfilterEntry {
        return {
            name: this.getBadgeName(currentHistoryEntries),
            filter: JSON.stringify(filter),
        };
    }

    private updateCookieHistory(newEntry: QuickfilterEntry, slug?: string): QuickfilterEntry[] {
        const existing = this.getCookieHistory();

        const newContent: QuickfilterEntry[] = [newEntry, ...existing].slice(0, this.QUICKFILTER_MAX_ENTRIES);

        this.cookieService.set(
            this.QUICKFILTER_COOKIE_NAME,
            JSON.stringify(newContent),
            this.QUICKFILTER_MAX_AGE_DAYS,
            slug ? `/${slug}` : '/',
        );

        return newContent;
    }

    private getCookieHistory(): QuickfilterEntry[] {
        if (!this.cookieService.has(this.QUICKFILTER_COOKIE_NAME)) {
            return [];
        }

        const raw = this.cookieService.get(this.QUICKFILTER_COOKIE_NAME);

        if (!raw) {
            return [];
        }

        try {
            return JSON.parse(raw) as QuickfilterEntry[];
        } catch {
            return [];
        }
    }

    private getBadgeName(currentHistoryEntries: Partial<Record<keyof ApiTypes.FilterObject, string>>): string {
        const entries = Object.entries(currentHistoryEntries).filter(([, v]) => Boolean(v));
        const count = entries.length;
        if (count === 0) {
            return 'No Filter';
        }
        if (count === 1) {
            return this.trimValue(entries[0]);
        }

        return this.formatMultipleEntries(entries, count, currentHistoryEntries['model']);
    }

    private formatMultipleEntries(entries: Array<[string, string]>, count: number, model?: string): string {
        const getModelEntry = () => ['model', model as string] as [string, string];
        const getOtherEntry = () => entries.filter(([key]) => key !== 'model')[0] ?? getModelEntry();

        if (count === 2) {
            return this.formatTwoEntries(entries, model, getModelEntry, getOtherEntry);
        }

        const extra = `(+${count - this.ADDITIONAL_FILTERS_OFFSET} Filter)`;
        if (model && entries.some(([key]) => key === 'model')) {
            return `${this.trimValue(getModelEntry())} | ${this.trimValue(getOtherEntry())} ${extra}`;
        }
        return `${this.trimValue(entries[0])} | ${this.trimValue(entries[1])} ${extra}`;
    }

    private formatTwoEntries(
        entries: Array<[string, string]>,
        model: string | undefined,
        getModelEntry: () => [string, string],
        getOtherEntry: () => [string, string],
    ): string {
        if (model && entries.some(([key]) => key === 'model')) {
            return `${this.trimValue(getModelEntry())} | ${this.trimValue(getOtherEntry())}`;
        }
        return `${this.trimValue(entries[0])} | ${this.trimValue(entries[1])}`;
    }

    private trimValue([key, value]: [string, string]): string {
        if (value.length <= this.MAX_DISPLAY_LENGTH || key === 'price') {
            return value;
        }
        return value.slice(0, this.MAX_DISPLAY_LENGTH) + '...';
    }
}
